import { useEffect, useState } from "react";
import {
  CircularProgress,
  Button,
  TextField,
  Typography,
  Container,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import authService from "../services/auth.service";
import { Helmet } from "react-helmet";
import { showMessage } from "../customhooks/useNotifier";

const Signin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const message = sessionStorage.getItem("logoutMessage");
    if (message) {
      showMessage(message, "success");
      sessionStorage.removeItem("logoutMessage");
    }
  }, []);

  // ✅ Clear localStorage on visiting login page
  useEffect(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("websiteData");
    localStorage.removeItem("userSession");
  }, []);

  const validateEmail = (value) => {
    setEmail(value);
    let error = "";

    if (!value) {
      error = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      error = "Enter a valid email address";
    }

    setEmailError(error);
    checkFormValidity(value, password, error);
  };

  const validatePassword = (value) => {
    setPassword(value);
    checkFormValidity(email, value, emailError);
  };

  const checkFormValidity = (email, password, emailError) => {
    setIsButtonDisabled(!email || !!emailError || !password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (email && password) {
      setLoading(true);
      try {
        await authService.login({ email, password });
        navigate("/app/");
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log("Login error: ", error.message);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In | Sitepilot</title>
        <meta
          name="description"
          content="Sign in to your Sitepilot dashboard to manage your AI chat analytics and user engagement insights."
        />
        <meta
          name="keywords"
          content="sign in, login, sitepilot, AI analytics, chat insights, user engagement, dashboard"
        />
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            animation: "float 20s ease-in-out infinite",
          },
        }}
      >
        <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
          <Card
            sx={{
              borderRadius: 4,
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              padding: 4,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              border: "none",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px 16px 0 0",
              },
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            )}

            <CardContent sx={{ padding: 0 }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    letterSpacing: "-0.02em",
                    mb: 1,
                  }}
                >
                  Welcome back
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: "1.1rem" }}
                >
                  Sign in to your Sitepilot App
                </Typography>
              </Box>
              <form onSubmit={handleSubmit} noValidate>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={email}
                  onChange={(e) => validateEmail(e.target.value)}
                  error={!!emailError}
                  helperText={emailError}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={password}
                  onChange={(e) => validatePassword(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    marginTop: 4,
                    padding: 2,
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                    textTransform: "none",
                    letterSpacing: "0.5px",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                      boxShadow: "0 12px 40px rgba(102, 126, 234, 0.4)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "#e2e8f0",
                      color: "#94a3b8",
                      boxShadow: "none",
                      transform: "none",
                    },
                  }}
                  disabled={isButtonDisabled}
                >
                  Sign In
                </Button>

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ marginTop: 3, color: "text.secondary" }}
                >
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    style={{
                      color: "#667eea",
                      fontWeight: 600,
                      textDecoration: "none",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#5a6fd8")}
                    onMouseLeave={(e) => (e.target.style.color = "#667eea")}
                  >
                    Sign Up
                  </Link>
                </Typography>

                {/* Google form */}
                <Box sx={{ textAlign: "center", marginTop: 3 }}>
                  <a
                    href="https://forms.gle/CxRPmiCp1Nkjvb7L7"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: "100%",
                      display: "inline-block",
                      background:
                        "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                      color: "#fff",
                      padding: "12px 24px",
                      borderRadius: "12px",
                      textDecoration: "none",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #334155 0%, #475569 100%)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 20px rgba(0, 0, 0, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        "linear-gradient(135deg, #1e293b 0%, #334155 100%)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.15)";
                    }}
                  >
                    Request a Meeting
                  </a>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default Signin;
